// ─────────────────────────────────────────────────────────────────────────────
// consentManager.ts
// Hoy Toca Recetas — Grupo Herdez
//
// Responsabilidades:
//   · Definir los tipos de consentimiento por categoría
//   · Leer / escribir el estado en localStorage
//   · Aplicar o bloquear scripts de terceros según el estado guardado
//   · Manejar versionado y expiración del consentimiento
// ─────────────────────────────────────────────────────────────────────────────

// ── Tipos ────────────────────────────────────────────────────────────────────

export type ConsentCategory = 'technical' | 'analytics' | 'marketing';

export interface ConsentState {
  /** El usuario ya tomó una decisión explícita */
  decided: boolean;
  /** Siempre true; no se puede desactivar */
  technical: boolean;
  /** Google Analytics, Hotjar, etc. */
  analytics: boolean;
  /** Meta Pixel, remarketing, etc. */
  marketing: boolean;
  /** Unix ms de cuando se guardó */
  timestamp: number;
  /** Versión del esquema; bump para forzar re-consent */
  version: string;
}

// ── Constantes ───────────────────────────────────────────────────────────────

const CONSENT_KEY = 'hoytoca_cookie_consent';

/**
 * Incrementar esta versión cuando cambien las categorías o la política,
 * para que los usuarios vean el banner nuevamente.
 */
export const CONSENT_VERSION = '1.0';

/** Días antes de volver a solicitar el consentimiento */
const CONSENT_TTL_DAYS = 180;

// ── Estado inicial ────────────────────────────────────────────────────────────

export const defaultConsent = (): ConsentState => ({
  decided:   false,
  technical: true,   // siempre habilitado
  analytics: false,
  marketing: false,
  timestamp: Date.now(),
  version:   CONSENT_VERSION,
});

// ── Persistencia ──────────────────────────────────────────────────────────────

/**
 * Lee el consentimiento guardado en localStorage.
 * Retorna null si:
 *   · No existe
 *   · La versión no coincide (política actualizada)
 *   · Han pasado más de CONSENT_TTL_DAYS días
 */
export function loadConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;

    const parsed: ConsentState = JSON.parse(raw);

    // Versión desactualizada → re-pedir consentimiento
    if (parsed.version !== CONSENT_VERSION) return null;

    // TTL expirado → re-pedir consentimiento
    const ageInDays = (Date.now() - parsed.timestamp) / 86_400_000;
    if (ageInDays > CONSENT_TTL_DAYS) return null;

    return parsed;
  } catch {
    return null;
  }
}

/** Persiste el estado completo en localStorage */
export function saveConsent(state: ConsentState): void {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('[HoyToca] No se pudo guardar el consentimiento:', e);
  }
}

/** Elimina el consentimiento guardado (útil para testing o "retirar consentimiento") */
export function clearConsent(): void {
  localStorage.removeItem(CONSENT_KEY);
}

// ── Aplicación de scripts ─────────────────────────────────────────────────────

/**
 * Evalúa el estado y activa los scripts de terceros correspondientes.
 * Se llama al cargar la app (con el consent guardado) y cada vez
 * que el usuario actualiza sus preferencias.
 */
export function applyConsent(state: ConsentState): void {
  if (state.analytics) enableAnalytics();
  if (state.marketing) enableMarketing();
}

/**
 * Activa cookies / scripts analíticos.
 * Emite un evento de ventana para que cualquier módulo externo
 * (GA4, Hotjar, etc.) pueda suscribirse sin acoplar este archivo.
 *
 * Uso:
 *   window.addEventListener('hoytoca:analytics:enable', () => { ... });
 */
function enableAnalytics(): void {
  window.dispatchEvent(new CustomEvent('hoytoca:analytics:enable'));
}

/**
 * Activa cookies / scripts de marketing.
 * Emite un evento para Meta Pixel, Google Ads, etc.
 *
 * Uso:
 *   window.addEventListener('hoytoca:marketing:enable', () => { ... });
 */
function enableMarketing(): void {
  window.dispatchEvent(new CustomEvent('hoytoca:marketing:enable'));
}

// ── Helpers de consulta ───────────────────────────────────────────────────────

/** Retorna true si el usuario ya tomó una decisión (para no mostrar el banner) */
export function hasDecided(): boolean {
  return loadConsent()?.decided === true;
}

/** Retorna true si una categoría específica está habilitada */
export function isCategoryEnabled(category: ConsentCategory): boolean {
  const state = loadConsent();
  if (!state?.decided) return false;
  return state[category] === true;
}